import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  Stack,
  InputAdornment,
  Chip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { 
  getAllVideoAnalytics, 
  createVideoAnalytics, 
  getVideoAnalyticsByUser,
  deleteVideoAnalytics
} from "../../Utils/apiServices";
import ConfirmDialog from "../../Components/ConfirmDialog";

const VideoAnalytics = () => {
  const [videoAnalytics, setVideoAnalytics] = useState([]);
  const [filteredAnalytics, setFilteredAnalytics] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAnalyticId, setSelectedAnalyticId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [newAnalytic, setNewAnalytic] = useState({
    user_id: '',
    ai_response: '',
    video_url: ''
  });

  useEffect(() => {
    fetchAllAnalytics();
  }, []);

  const fetchAllAnalytics = () => {
    getAllVideoAnalytics().then((response) => {
      if (response.data.success) {
        setVideoAnalytics(response.data.data);
        setFilteredAnalytics(response.data.data);
      }
    }).catch((error) => {
      console.error('Error fetching video analytics:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching video analytics',
        severity: 'error'
      });
    });
  };

  const handleSearch = (event) => {
    const term = event.target.value.toLowerCase();
    setSearchTerm(term);
    
    const filtered = videoAnalytics.filter(analytic => 
      (analytic.user_id?.toLowerCase() || '').includes(term) ||
      (analytic.ai_response?.toLowerCase() || '').includes(term) ||
      (analytic.video_url?.toLowerCase() || '').includes(term)
    );
    
    setFilteredAnalytics(filtered);
  };

  const handleOpen = () => setOpen(true);
  
  const handleClose = () => {
    setOpen(false);
    setNewAnalytic({
      user_id: '',
      ai_response: '',
      video_url: ''
    });
  };

  const handleChange = (e) => {
    setNewAnalytic({
      ...newAnalytic,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async () => {
    try {
      const response = await createVideoAnalytics(newAnalytic);
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Video analytics created successfully',
          severity: 'success'
        });
        fetchAllAnalytics();
        handleClose();
      }
    } catch (error) {
      console.error('Error creating video analytics:', error);
      setSnackbar({
        open: true,
        message: 'Error creating video analytics',
        severity: 'error'
      });
    }
  };

  const handleSearchByUser = async () => {
    if (!searchTerm) {
      setSnackbar({
        open: true,
        message: 'Please enter a user ID to search',
        severity: 'warning'
      });
      return;
    }
    
    try {
      const response = await getVideoAnalyticsByUser(searchTerm);
      if (response.data.success) {
        setFilteredAnalytics(response.data.data);
        setSnackbar({
          open: true,
          message: `Found ${response.data.data.length} records for user`,
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error searching by user:', error);
      setSnackbar({
        open: true,
        message: 'Error searching by user',
        severity: 'error'
      });
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilteredAnalytics(videoAnalytics);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleDeleteClick = (id) => {
    setSelectedAnalyticId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setSelectedAnalyticId(null);
  };

  const handleDeleteConfirm = async () => {
    if (selectedAnalyticId == null) return;
    setDeleteLoading(true);
    try {
      await deleteVideoAnalytics(selectedAnalyticId);
      setSnackbar({
        open: true,
        message: 'Video analytics deleted successfully',
        severity: 'success'
      });
      fetchAllAnalytics();
      setDeleteDialogOpen(false);
      setSelectedAnalyticId(null);
    } catch (error) {
      console.error('Error deleting video analytics:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting video analytics',
        severity: 'error'
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          p: 2.5,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 50%, #3b82f6 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 8px 24px rgba(168,85,247,0.3)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -40,
            right: -40,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          },
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.3 }}>🎬 Video Analytics</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>AI-powered video analysis records and insights</Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1 }}>{videoAnalytics.length}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Records</Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1} sx={{ flex: 1, maxWidth: { sm: 500 } }}>
            <TextField
              placeholder="Search by User ID..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearch}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="outlined" onClick={handleSearchByUser} size="small" sx={{ flexShrink: 0 }}>
              Search
            </Button>
            <Button variant="outlined" onClick={handleClearSearch} size="small" color="inherit" sx={{ flexShrink: 0, borderColor: 'divider', color: 'text.secondary' }}>
              Clear
            </Button>
          </Stack>
          <Button
            variant="contained"
            onClick={handleOpen}
            startIcon={<AddIcon />}
            sx={{ flexShrink: 0 }}
          >
            Add Record
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Showing {filteredAnalytics.length} of {videoAnalytics.length} records
        </Typography>
      </Paper>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create Video Analytics</DialogTitle>
        <DialogContent>
          <TextField
            name="user_id"
            label="User ID"
            fullWidth
            margin="dense"
            value={newAnalytic.user_id}
            onChange={handleChange}
            required
          />
          <TextField
            name="ai_response"
            label="AI Response"
            fullWidth
            margin="dense"
            multiline
            rows={4}
            value={newAnalytic.ai_response}
            onChange={handleChange}
            required
          />
          <TextField
            name="video_url"
            label="Video URL"
            fullWidth
            margin="dense"
            value={newAnalytic.video_url}
            onChange={handleChange}
            placeholder="https://..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!newAnalytic.user_id || !newAnalytic.ai_response}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Confirm delete"
        message="Are you sure you want to delete this video analytics record?"
        confirmLabel="Delete"
        onCancel={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
      />

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: 'grey.50',
                  color: 'text.secondary',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  letterSpacing: '0.02em',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  py: 1.5,
                },
              }}
            >
              <TableCell>ID</TableCell>
              <TableCell>User ID</TableCell>
              <TableCell>AI Response</TableCell>
              <TableCell>Video URL</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Updated At</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAnalytics.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  No video analytics found
                </TableCell>
              </TableRow>
            ) : (
              filteredAnalytics.map((analytic) => (
                <TableRow
                  key={analytic.id}
                  hover
                  sx={{ '&:last-child td': { borderBottom: 0 }, transition: 'background-color 0.15s' }}
                >
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{analytic.id}</TableCell>
                  <TableCell>
                    <Chip label={analytic.user_id} size="small" variant="outlined" sx={{ fontWeight: 500 }} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                    {analytic.ai_response}
                  </TableCell>
                  <TableCell>
                    {analytic.video_url ? (
                      <Button
                        component="a"
                        href={analytic.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      >
                        View Video
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.disabled">No video</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>{formatDate(analytic.created_at)}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>{formatDate(analytic.updated_at)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => handleDeleteClick(analytic.id)}
                      color="error"
                      size="small"
                      sx={{ border: '1px solid', borderColor: 'divider' }}
                      aria-label="Delete record"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VideoAnalytics;

